import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, BookOpen } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const InsuranceGlossary = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const glossaryTerms = [
    {
      term: 'Sum Insured',
      definition: 'The maximum amount your insurer will pay for a claim under your policy. This should represent the full replacement cost of your contents, not their second-hand value.',
      category: 'Coverage'
    },
    {
      term: 'Excess (Deductible)',
      definition: 'The amount you pay out-of-pocket before your insurance coverage kicks in. For example, if you have a $500 excess and make a $2,000 claim, you pay $500 and the insurer pays $1,500.',
      category: 'Claims'
    },
    {
      term: 'Premium',
      definition: 'The amount you pay (usually monthly or annually) to maintain your insurance coverage. Think of it as your contribution to the shared risk pool.',
      category: 'Policy'
    },
    {
      term: 'Contents Insurance',
      definition: 'Coverage for your personal belongings inside your home (furniture, electronics, clothing, etc.). This is separate from building insurance which covers the structure itself.',
      category: 'Coverage'
    },
    {
      term: 'Replacement Value',
      definition: 'The cost to replace an item with a new one of similar kind and quality at today\'s prices. This differs from "market value" which considers depreciation.',
      category: 'Valuation'
    },
    {
      term: 'Policy Schedule',
      definition: 'The document that lists specific details about your coverage including: sum insured, excess amounts, premium, cover period, and any special conditions or endorsements.',
      category: 'Policy'
    },
    {
      term: 'Exclusions',
      definition: 'Specific situations, items, or events that your policy does NOT cover. Always read these carefully as they vary between policies.',
      category: 'Coverage'
    },
    {
      term: 'Accidental Damage',
      definition: 'Coverage for unexpected damage that wasn\'t deliberate (like spilling wine on your carpet). This is often an optional extra that increases your premium.',
      category: 'Coverage'
    },
    {
      term: 'Proof of Ownership',
      definition: 'Evidence that you owned an item (receipts, photos, bank statements, warranties). Essential when making a claim, especially for high-value items.',
      category: 'Claims'
    },
    {
      term: 'Underinsured',
      definition: 'When your sum insured is less than the actual replacement cost of your contents. This can leave you out of pocket if you need to claim.',
      category: 'Risk'
    },
    {
      term: 'Specified Items',
      definition: 'High-value items (jewelry, art, electronics) that you list separately on your policy, often with individual coverage limits and sometimes requiring valuations.',
      category: 'Coverage'
    },
    {
      term: 'Claim',
      definition: 'A formal request to your insurer for payment or reimbursement for a covered loss. You\'ll need to provide evidence of the loss and its value.',
      category: 'Claims'
    },
    {
      term: 'Policy Renewal',
      definition: 'When your insurance policy term ends and you choose to continue coverage for another period. This is a good time to review your sum insured and coverage needs.',
      category: 'Policy'
    },
    {
      term: 'Peril',
      definition: 'A specific risk or cause of loss covered by your policy (fire, theft, storm damage, etc.). Your policy document lists which perils are covered.',
      category: 'Coverage'
    },
    {
      term: 'Betterment',
      definition: 'When a claim results in an improvement to what you had before (e.g., replacing old carpet with new). Insurers may apply betterment reductions to reflect this.',
      category: 'Claims'
    }
  ];

  const filteredTerms = glossaryTerms.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(glossaryTerms.map(term => term.category))];

  return (
    <section id="glossary" data-testid="insurance-glossary" className="py-28 bg-ink border-t rule-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
          <p className="eyebrow text-emerald-400">Knowledge base</p>
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            Insurance Glossary
          </h2>
          <p className="text-xl text-slate-400">
            Understanding the language of insurance helps you make better decisions
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-6">
          {/* Search box */}
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardContent className="p-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search terms (e.g., 'excess', 'claim', 'coverage')..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="glossary-search"
                  className="pl-12 py-6 text-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                />
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <div className="text-center text-slate-400">
            {searchTerm && (
              <p>Found {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''}</p>
            )}
          </div>

          {/* Glossary terms */}
          <Card className="border-2 border-slate-200 shadow-xl">
            <CardHeader className="bg-white border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">Common Insurance Terms</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {filteredTerms.length > 0 ? (
                <Accordion type="single" collapsible className="w-full space-y-2">
                  {filteredTerms.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="border border-slate-200 rounded-lg px-4 hover:border-cyan-400 transition-colors"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="text-lg font-semibold text-slate-900 text-left">{item.term}</span>
                          <Badge variant="outline" className="ml-4 text-xs">
                            {item.category}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4 pt-2">
                        <p className="text-slate-600 leading-relaxed">{item.definition}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg">No terms found matching "{searchTerm}"</p>
                  <p className="text-slate-400 text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {/* Category badges */}
              {!searchTerm && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-3 font-medium">Browse by category:</p>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="cursor-pointer hover:bg-cyan-50 hover:border-cyan-400 transition-colors"
                        onClick={() => setSearchTerm(category)}
                      >
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <p className="text-sm text-slate-600 text-center">
              <span className="font-semibold">Note:</span> Definitions provided are general explanations. 
              Specific terms and conditions vary between insurers and policies. Always refer to your policy documents 
              and consult your insurer or broker for clarification.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsuranceGlossary;
